import NumberInput from '../../../components/NumberInput'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2, Check, X, TrendingUp, TrendingDown } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useUnits } from '../../../contexts/UnitsContext'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'
import PendingSyncBadge from '../../../components/PendingSyncBadge'

export function WeightHistoryItem({ entry, onUpdate, onDelete }) {
  const { t } = useTranslation()
  const { weightUnit, formatWeight, displayValueWeight, weightToKg } = useUnits()
  const confirm = useConfirm()
  const [editing, setEditing] = useState(false)
  const [editWeight, setEditWeight] = useState(String(displayValueWeight(entry.weight).toFixed(2)))
  const [editNote, setEditNote] = useState(entry.note || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    const val = Number(editWeight)
    if (!val || val <= 0) return
    setSaving(true)
    const kgValue = weightToKg(val)
    const success = await onUpdate(entry.id, kgValue, editNote.trim() || null)
    if (success) setEditing(false)
    setSaving(false)
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('phoenixTracker.weight.confirmDelete'),
      description: t('phoenixTracker.weight.confirmDeleteDesc'),
      confirmLabel: t('phoenixTracker.weight.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(entry.id)
  }

  const diffColor = entry.diff === null
    ? ''
    : entry.diff < 0
      ? 'text-green-500'
      : entry.diff > 0
        ? 'text-red-500'
        : 'text-muted-foreground'

  const DiffIcon = entry.diff === null
    ? null
    : entry.diff < 0
      ? TrendingDown
      : entry.diff > 0
        ? TrendingUp
        : null

  return (
    <div className="p-3 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        {/* Date */}
        <div className="text-sm text-muted-foreground font-mono min-w-[90px] flex items-center gap-1.5">
          {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
          <PendingSyncBadge mutation={entry._pending} />
        </div>

        {/* Weight / Edit input */}
        <div className="flex-1">
          {editing ? (
            <NumberInput
              inputMode="decimal"
              step="any"
              value={editWeight}
              onChange={(e) => setEditWeight(e.target.value)}
              className="h-9 w-24 px-2 rounded border border-app-primary-500 bg-background text-foreground font-mono text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
                if (e.key === 'Escape') setEditing(false)
              }}
            />
          ) : (
            <span className="font-mono font-semibold text-foreground">
              {formatWeight(entry.weight)} <span className="text-xs text-muted-foreground ms-1">{weightUnit}</span>
            </span>
          )}
        </div>

        {/* Diff badge */}
        {entry.diff !== null && !editing && (
          <div className={cn('flex items-center gap-1 text-xs font-mono', diffColor)}>
            {DiffIcon && <DiffIcon className="w-3 h-3" />}
            <span>{entry.diff > 0 ? '+' : ''}{formatWeight(entry.diff)}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="p-2 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 transition-colors"
                aria-label={t('phoenixTracker.weight.save')}
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setEditing(false); setEditWeight(String(displayValueWeight(entry.weight).toFixed(2))); setEditNote(entry.note || '') }}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-muted-foreground transition-colors"
                aria-label={t('phoenixTracker.weight.cancel')}
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setEditing(true); setEditWeight(String(displayValueWeight(entry.weight).toFixed(2))); setEditNote(entry.note || '') }}
                className="p-2 rounded-md hover:bg-app-primary-100 dark:hover:bg-app-primary-900/30 text-app-primary-500 transition-colors"
                aria-label={t('phoenixTracker.weight.edit')}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={handleDelete}
                className="p-2 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                aria-label={t('phoenixTracker.weight.delete')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Note: editable in edit mode, display when present */}
      {editing ? (
        <textarea
          value={editNote}
          onChange={(e) => setEditNote(e.target.value)}
          placeholder={t('phoenixTracker.weight.notePlaceholder')}
          rows={2}
          className="mt-2 w-full px-2 py-1 rounded border border-app-primary-500 bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-y"
        />
      ) : entry.note ? (
        <p className="mt-2 text-xs italic text-muted-foreground whitespace-pre-wrap break-words">
          {entry.note}
        </p>
      ) : null}
    </div>
  )
}
